import React, {
  useState,
  useEffect
} from "react";

import { Rnd } from "react-rnd";

import "./MDSFaceplate.css";

/* =========================================================
   HELPERS
========================================================= */

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

/* =========================================================
   MAIN COMPONENT
========================================================= */

function AnalyzerV201AFaceplate({

  close = () => {},

  defaultPosition = {
    x: 220,
    y: 100
  }

}) {

  /* =========================================================
     ACTIVE TAB
  ========================================================= */

  const [activeTab, setActiveTab] =
    useState("inletParameters");

  /* =========================================================
     FACEPLATE POSITION
  ========================================================= */

  const [faceplatePos, setFaceplatePos] =
    useState(() => {

      const saved =
        localStorage.getItem(
          "AnalyzerV201A_faceplatePos"
        );

      return saved
        ? JSON.parse(saved)
        : defaultPosition;

    });

  /* =========================================================
     FACEPLATE SIZE
  ========================================================= */

  const [faceplateSize, setFaceplateSize] =
    useState(() => {

      const saved =
        localStorage.getItem(
          "AnalyzerV201A_faceplateSize"
        );

      return saved
        ? JSON.parse(saved)
        : {
            width: 900,
            height: 700
          };

    });

  /* =========================================================
     SAVE POSITION
  ========================================================= */

  useEffect(() => {

    localStorage.setItem(
      "AnalyzerV201A_faceplatePos",
      JSON.stringify(faceplatePos)
    );

  }, [faceplatePos]);

  /* =========================================================
     SAVE SIZE
  ========================================================= */

  useEffect(() => {

    localStorage.setItem(
      "AnalyzerV201A_faceplateSize",
      JSON.stringify(faceplateSize)
    );

  }, [faceplateSize]);

  /* =========================================================
     FAST LOCAL STORAGE POLLING
     (ONE INTERVAL ONLY TO PREVENT FREEZING)
  ========================================================= */

  const [processData, setProcessData] =
    useState({

      FIC_12001: 0,
      FIC_12002: 0,
      FIC_12003: 0,
      FIC_12004: 0,
      FIC_12005: 0,

      ZI_12001: 0,
      TT_12001: 0,

      MixValveOpening: 0,

      Analyzer1_salt: 10,
      Analyzer1_bsw: 0.1,
      Analyzer1_water: 0.1,
      Analyzer1_sediment: 0.01,
      Analyzer1_h2s: 0.9,

      Analyzer1_inorganic_chlorides: 1,
      Analyzer1_organic_chlorides: 0.95

    });


    /* ===========================================
   CRUDE FROM DAY TANK
=========================================== */

let crude = {};

try {

    crude =
        JSON.parse(
            localStorage.getItem("V1502_CRUDE") ||
            localStorage.getItem("V1501_CRUDE") ||
            "{}"
        ) || {};

}
catch{

    crude = {};

}



  useEffect(() => {

    const interval = setInterval(() => {

      setProcessData({

        FIC_12001:
          Number(
            localStorage.getItem(
              "FIC_12001"
            )
          ) || 0,

        FIC_12002:
          Number(
            localStorage.getItem(
              "FIC_12002"
            )
          ) || 0,

        FIC_12003:
          Number(
            localStorage.getItem(
              "FIC_12003"
            )
          ) || 0,

        FIC_12004:
          Number(
            localStorage.getItem(
              "FIC_12004"
            )
          ) || 0,

        FIC_12005:
          Number(
            localStorage.getItem(
              "FIC_12005"
            )
          ) || 0,

        ZI_12001:
          Number(
            localStorage.getItem(
              "ZI_12001"
            )
          ) || 0,

        TT_12001:
          Number(
            localStorage.getItem(
              "TT_12001"
            )
          ) || 0,

        MixValveOpening:
          Number(
            localStorage.getItem(
              "MixValveOpening"
            )
          ) || 0,

        Analyzer1_salt:
          Number(
          crude?.physicalProperties?.[19]?.value ??
          crude?.physicalProperties?.[19]?.valueMax
          ) || 10,

          Analyzer1_bsw:
          Number(
          crude?.physicalProperties?.[22]?.value
          ) || 0.1,

          Analyzer1_water:
          Number(
          crude?.physicalProperties?.[20]?.value
          ) || 0.1,

          Analyzer1_sediment:
          Number(
          crude?.physicalProperties?.[21]?.value
          ) || 0.01,

          Analyzer1_h2s:
          Number(
          crude?.corrosionStability?.[27]?.value
          ) || 0.9,

          Analyzer1_inorganic_chlorides:
          Number(
          crude?.corrosionStability?.[30]?.value
          ) || 1,

          Analyzer1_organic_chlorides:
          Number(
          crude?.corrosionStability?.[29]?.value
          ) || 0.95,

      });

    }, 500);

    return () =>
      clearInterval(interval);

  }, []);

  /* =========================================================
     DCS INPUTS
  ========================================================= */

  const crudeFlow =
    processData.FIC_12001;

  const decalcifierFlow =
    processData.FIC_12002;

  const demulsifierFlow =
    processData.FIC_12003;

  const washWaterFlow =
    processData.FIC_12004;

  const rinseWaterFlow =
    processData.FIC_12005;

  const gridVoltage =
    processData.ZI_12001;

  const desalterTemp =
    processData.TT_12001;

  const mixingValveOpening =
    processData.MixValveOpening;

  /* =========================================================
     INLET CRUDE
  ========================================================= */

  const inletSalt =
    processData.Analyzer1_salt;

  const inletBSW =
    processData.Analyzer1_bsw;

  const inletWater =
    processData.Analyzer1_water;

  const inletSediment =
    processData.Analyzer1_sediment;

  const inletH2S =
    processData.Analyzer1_h2s;

  const inletInorganicChlorides =
    processData.Analyzer1_inorganic_chlorides;

  const inletOrganicChlorides =
    processData.Analyzer1_organic_chlorides;

  /* =========================================================
     RATIOS
  ========================================================= */

  const washWaterRatio =
    crudeFlow > 0
      ? washWaterFlow / crudeFlow
      : 0;

  /* =========================================================
     EFFICIENCIES
  ========================================================= */

  const gridEfficiency =
    clamp(
      (gridVoltage / 35) * 100,
      0,
      100
    );

  let washEfficiency = 0;

  if(
    washWaterRatio >= 0.04 &&
    washWaterRatio <= 0.06
  ){

    washEfficiency = 100;

  }
  else if(
    washWaterRatio > 0
  ){

    washEfficiency = 65;

  }

  let mixingEfficiency = 0;

  if(
    mixingValveOpening >= 0.35 &&
    mixingValveOpening <= 0.65
  ){

    mixingEfficiency = 100;

  }
  else if(
    mixingValveOpening > 0
  ){

    mixingEfficiency = 60;

  }

  const demulsifierEfficiency =
    demulsifierFlow > 0
      ? 100
      : 20;

  const decalcifierEfficiency =
    decalcifierFlow > 0
      ? 100
      : 30;

  let temperatureEfficiency = 0;

  if(
    desalterTemp >= 120 &&
    desalterTemp <= 140
  ){

    temperatureEfficiency = 100;

  }
  else if(
    desalterTemp >= 90
  ){

    temperatureEfficiency = 70;

  }
  else{

    temperatureEfficiency = 30;

  }

  /* =========================================================
     OVERALL EFFICIENCY
  ========================================================= */

  const overallEfficiency =
  (
    gridEfficiency +
    washEfficiency +
    mixingEfficiency +
    demulsifierEfficiency +
    decalcifierEfficiency +
    temperatureEfficiency
  ) / 6;

  /* =========================================================
     OUTLET PARAMETERS
  ========================================================= */

  const saltOut =
  (
    inletSalt *
    (
      1 -
      (
        overallEfficiency / 100
      ) * 0.85
    )
  ).toFixed(2);

  const bswOut =
  (
    inletBSW *
    (
      1 -
      (
        overallEfficiency / 100
      ) * 0.60
    )
  ).toFixed(3);

  const waterOut =
  (
    inletWater *
    (
      1 -
      (
        overallEfficiency / 100
      ) * 0.75
    )
  ).toFixed(3);

  const sedimentOut =
  (
    inletSediment *
    (
      1 -
      (
        overallEfficiency / 100
      ) * 0.25
    )
  ).toFixed(4);

  const inorganicChloridesOut =
  (
    inletInorganicChlorides *
    (
      1 -
      (
        overallEfficiency / 100
      ) * 0.90
    )
  ).toFixed(2);

  const organicChloridesOut =
  (
    inletOrganicChlorides *
    (
      1 -
      (
        overallEfficiency / 100
      ) * 0.05
    )
  ).toFixed(2);

  const h2sOut =
  (
    inletH2S *
    (
      1 -
      (
        overallEfficiency / 100
      ) * 0.10
    )
  ).toFixed(2);


useEffect(() => {localStorage.setItem("saltOut", saltOut);}, [saltOut]);
useEffect(() => {localStorage.setItem("bswOut", bswOut);}, [bswOut]);
useEffect(() => {localStorage.setItem("waterOut", waterOut);}, [waterOut]);
useEffect(() => {localStorage.setItem("sedimentOut", sedimentOut);}, [sedimentOut]);
useEffect(() => {localStorage.setItem("inorganicChloridesOut", inorganicChloridesOut);}, [inorganicChloridesOut]);
useEffect(() => {localStorage.setItem("organicChloridesOut", organicChloridesOut);}, [organicChloridesOut]);
useEffect(() => {localStorage.setItem("h2sOut", h2sOut);}, [h2sOut]);




  /* =========================================================
     UPSET CONDITIONS
  ========================================================= */

  const upsetConditions = [];

  if(gridVoltage <= 0){

    upsetConditions.push({

      title: "GRID OFF",

      impact:
        "No electric discharge for proper dehydration and salt removal. Salt level rises."

    });

  }

  if(
    gridVoltage > 0 &&
    gridVoltage < 15
  ){

    upsetConditions.push({

      title: "LOW GRID VOLTAGE",

      impact:
        "Weak electrostatic coalescence causing poor dehydration efficiency."

    });

  }

  if(washWaterFlow <= 0){

    upsetConditions.push({

      title: "NO WASH WATER",

      impact:
        "No salt extraction medium available. Desalting efficiency collapses."

    });

  }

  if(mixingValveOpening > 0.80){

    upsetConditions.push({

      title: "EXCESSIVE MIXING",

      impact:
        "Tight emulsion formation causing rag layer growth and BS&W increase."

    });

  }

  if(demulsifierFlow <= 0){

    upsetConditions.push({

      title: "LOW DEMULSIFIER",

      impact:
        "Poor emulsion breaking causes rag layer growth and unstable interface."

    });

  }

  if(decalcifierFlow <= 0){

    upsetConditions.push({

      title: "LOW DECALCIFIER",

      impact:
        "Calcium salts remain in crude causing downstream fouling."

    });

  }

  if(desalterTemp < 110){

    upsetConditions.push({

      title: "LOW TEMPERATURE",

      impact:
        "High crude viscosity reduces water settling and electrostatic separation."

    });

  }

  if(washWaterRatio > 0.10){

    upsetConditions.push({

      title: "HIGH WATER INTERFACE",

      impact:
        "Water carryover risk and possible electrical arcing."

    });

  }

  const desalterStatus =
    upsetConditions.length === 0
      ? "GOOD DESALTER CONDITION"
      : "DESALTER UPSET CONDITION";

  /* =========================================================
     DATA ROW COMPONENT
  ========================================================= */

  const DataRow = ({
    label,
    value,
    unit
  }) => (

    <div className="mds-row">

      <div className="mds-key">
        {label}
      </div>

      <div className="mds-value-wrapper">

        <div className="mds-value-box">
          {value}
        </div>

        <div className="mds-unit-box">
          {unit}
        </div>

      </div>

    </div>

  );

  /* =========================================================
     SECTION RENDERER
  ========================================================= */

  const renderSection = () => {

    switch(activeTab){

      case "inletParameters":

        return (

          <div className="mds-section">

            <h3 className="mds-section-title">
              INLET PARAMETERS
            </h3>

            <div className="mds-grid">

              <DataRow
                label="Crude Flow"
                value={crudeFlow}
                unit="BPM"
              />

              <DataRow
                label="Wash Water Flow"
                value={washWaterFlow}
                unit="BPM"
              />

              <DataRow
                label="Rinse Water Flow"
                value={rinseWaterFlow}
                unit="BPM"
              />

              <DataRow
                label="Demulsifier Flow"
                value={demulsifierFlow}
                unit="L/H"
              />

              <DataRow
                label="Decalcifier Flow"
                value={decalcifierFlow}
                unit="L/H"
              />

              <DataRow
                label="Grid Voltage"
                value={gridVoltage}
                unit="kV"
              />

              <DataRow
                label="Temperature"
                value={desalterTemp}
                unit="°C"
              />

              <DataRow
                label="Mix Valve Opening"
                value={(mixingValveOpening * 100).toFixed(0)}
                unit="%"
              />

            </div>

          </div>

        );

      case "inletCrude":

        return (

          <div className="mds-section">

            <h3 className="mds-section-title">
              INLET CRUDE PARAMETERS
            </h3>

            <div className="mds-grid">

              <DataRow label="Salt" value={inletSalt} unit="PTB" />
              <DataRow label="BS&W" value={inletBSW} unit="%" />
              <DataRow label="Water" value={inletWater} unit="%" />
              <DataRow label="Sediment" value={inletSediment} unit="%" />

              <DataRow
                label="Inorganic Chlorides"
                value={inletInorganicChlorides}
                unit="ppm"
              />

              <DataRow
                label="Organic Chlorides"
                value={inletOrganicChlorides}
                unit="ppm"
              />

              <DataRow
                label="H₂S"
                value={inletH2S}
                unit="mg/kg"
              />

            </div>

          </div>

        );

      case "outletAnalyzer":

        return (

          <div className="mds-section">

            <h3 className="mds-section-title">
              OUTLET ANALYZER
            </h3>

            <div className="mds-grid">

              <DataRow label="Salt" value={saltOut} unit="PTB" />
              <DataRow label="BS&W" value={bswOut} unit="%" />
              <DataRow label="Water" value={waterOut} unit="%" />
              <DataRow label="Sediment" value={sedimentOut} unit="%" />

              <DataRow
                label="Inorganic Chlorides"
                value={inorganicChloridesOut}
                unit="ppm"
              />

              <DataRow
                label="Organic Chlorides"
                value={organicChloridesOut}
                unit="ppm"
              />

              <DataRow
                label="H₂S"
                value={h2sOut}
                unit="mg/kg"
              />

            </div>

          </div>

        );

      case "performance":

        return (

          <div className="mds-section">

            <h3 className="mds-section-title">
              DESALTER PERFORMANCE
            </h3>

            <div className="mds-grid">

              <DataRow
                label="Desalter Status"
                value={desalterStatus}
                unit="-"
              />

              <DataRow
                label="Grid Efficiency"
                value={gridEfficiency.toFixed(0)}
                unit="%"
              />

              <DataRow
                label="Wash Water Efficiency"
                value={washEfficiency.toFixed(0)}
                unit="%"
              />

              <DataRow
                label="Mixing Efficiency"
                value={mixingEfficiency.toFixed(0)}
                unit="%"
              />

              <DataRow
                label="Demulsifier Efficiency"
                value={demulsifierEfficiency.toFixed(0)}
                unit="%"
              />

              <DataRow
                label="Decalcifier Efficiency"
                value={decalcifierEfficiency.toFixed(0)}
                unit="%"
              />

              <DataRow
                label="Temperature Efficiency"
                value={temperatureEfficiency.toFixed(0)}
                unit="%"
              />

              <DataRow
                label="Overall Efficiency"
                value={overallEfficiency.toFixed(0)}
                unit="%"
              />

            </div>

          </div>

        );

      case "upsetMonitor":

        return (

          <div className="mds-section">

            <h3 className="mds-section-title">
              FAILURE / UPSET MONITOR
            </h3>

            <div className="mds-grid">

              {
                upsetConditions.length === 0 ? (

                  <DataRow
                    label="GOOD CONDITION"
                    value="Stable Operation"
                    unit="OK"
                  />

                ) : (

                  upsetConditions.map(
                    (item, index) => (

                      <div
                        key={index}
                        className="mds-row"
                      >

                        <div className="mds-key">
                          {item.title}
                        </div>

                        <div className="mds-value-wrapper">

                          <div
                            className="mds-value-box"
                            style={{
                              width: "100%",
                              minHeight: "55px",
                              whiteSpace: "normal",
                              lineHeight: "18px"
                            }}
                          >
                            {item.impact}
                          </div>

                        </div>

                      </div>

                    )
                  )

                )
              }

            </div>

          </div>

        );

      default:
        return null;

    }

  };

  /* =========================================================
     UI
  ========================================================= */

  return (

    <Rnd

      size={faceplateSize}

      position={faceplatePos}

      onDragStop={(e, d) => {

        setFaceplatePos({
          x: d.x,
          y: d.y
        });

      }}

      onResizeStop={(
        e,
        direction,
        ref,
        delta,
        position
      ) => {

        setFaceplateSize({

          width: ref.offsetWidth,
          height: ref.offsetHeight

        });

        setFaceplatePos(position);

      }}

      minWidth={750}
      minHeight={500}

      bounds="window"

      enableUserSelectHack={false}

      dragHandleClassName="mds-header"

      className="mds-rnd"
    >

      <div
        className="mds-faceplate"
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column"
        }}
      >

        {/* =====================================================
            HEADER
        ===================================================== */}

        <div className="mds-header">

          <div className="mds-title">
            V-201A DESALTER ANALYZER
          </div>

          <button
            className="mds-close-btn"
            onClick={close}
          >
            ✕
          </button>

        </div>

        {/* =====================================================
            BODY
        ===================================================== */}

        <div
          className="mds-body"
          style={{
            flex: 1,
            minHeight: 0
          }}
        >

          {/* =================================================
              LEFT PANEL
          ================================================= */}

          <div className="mds-left-panel">

            {[
              ["inletParameters", "Inlet Parameters"],
              ["inletCrude", "Inlet Crude Parameters"],
              ["outletAnalyzer", "Outlet Analyzer"],
              ["performance", "Performance"],
              ["upsetMonitor", "Upset Monitor"]
            ].map(([key, label]) => (

              <div
                key={key}

                className={`mds-tab ${
                  activeTab === key
                    ? "active"
                    : ""
                }`}

                onClick={() =>
                  setActiveTab(key)
                }
              >

                {label}

              </div>

            ))}

          </div>

          {/* =================================================
              RIGHT PANEL
          ================================================= */}

          <div
            className="mds-right-panel"
            style={{
              overflowY: "auto",
              overflowX: "hidden",
              paddingRight: "8px",
              flex: 1,
              minHeight: 0
            }}
          >

            {renderSection()}

          </div>

        </div>

        {/* =====================================================
            FOOTER
        ===================================================== */}

        <div className="mds-footer">

          <div className="mds-footer-title">
            NK Refinery DCS and Digital Twin System
          </div>

        </div>

      </div>

    </Rnd>

  );

}

export default AnalyzerV201AFaceplate;